// Starred animals (plan smartkraal-owner-experience B2): the animals an owner
// wants to find first. A per-ranch set of animal ids kept on this device
// (localStorage), nothing on the server; the wall's "Starred" filter sends the
// ids as GET /cattle ?ids= (at most MAX_STARRED_IDS). One module-level store:
// every star button on a wall of hundreds of tiles reads the same snapshot, and
// a toggle parses the stored list once, not once per tile.
import { useSyncExternalStore } from 'react'

export const MAX_STARRED_IDS = 500
const STORAGE_KEY = (ranchId) => `cattle.starred.${ranchId || 'none'}`
const EVENT = 'cattle:stars'

const EMPTY = Object.freeze([])
const cache = new Map()          // ranchId -> frozen array (the snapshot)
const listeners = new Set()

function read(ranchId) {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY(ranchId)) || '[]')
    return Object.freeze(Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [])
  } catch {
    return EMPTY
  }
}

function snapshot(ranchId) {
  if (!cache.has(ranchId)) cache.set(ranchId, read(ranchId))
  return cache.get(ranchId)
}

function invalidate(ranchId) {
  if (ranchId == null) cache.clear()
  else cache.delete(ranchId)
  for (const fn of listeners) fn()
}

function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

if (typeof window !== 'undefined') {
  window.addEventListener(EVENT, (e) => invalidate(e.detail?.ranchId))
  window.addEventListener('storage', () => invalidate())      // another tab starred
}

/** The starred animal ids on this device for the ranch (a frozen array). */
export function starredIds(ranchId) {
  return snapshot(ranchId)
}

export function isStarred(ranchId, animalId) {
  return snapshot(ranchId).includes(animalId)
}

/** Star or unstar one animal; returns the new state. */
export function toggleStar(ranchId, animalId) {
  const ids = snapshot(ranchId)
  const next = ids.includes(animalId) ? ids.filter((x) => x !== animalId) : [...ids, animalId]
  try { localStorage.setItem(STORAGE_KEY(ranchId), JSON.stringify(next)) } catch { /* private mode: the star lasts the page */ }
  cache.set(ranchId, Object.freeze(next))
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { ranchId } })) } catch { /* no window */ }
  return next.includes(animalId)
}

/** The starred ids as React state: follows toggles on this page and in other tabs. */
export function useStarred(ranchId) {
  return useSyncExternalStore(subscribe, () => snapshot(ranchId), () => EMPTY)
}
